import { Logger } from '../../utils';

export interface IpInfo {
  ip: string;
  timestamp: number;
  source: 'api' | 'cached';
}

export class IpAddressService {
  private static readonly IP_CACHE_KEY = 'user_ip_info';
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private static readonly IP_API_ENDPOINTS = [
    'https://api.ipify.org?format=json',
    'https://ipapi.co/json/',
    'https://api.myip.com'
  ];

  static async getUserIP(): Promise<IpInfo> {
    // Try to get from cache first
    const cached = this.getCachedIP();
    if (cached && this.isCacheValid(cached)) {
      Logger.log('🔍 Using cached IP address:', cached.ip);
      return { ...cached, source: 'cached' };
    }

    // Try to fetch from multiple IP APIs
    for (const endpoint of this.IP_API_ENDPOINTS) {
      try {
        const ipInfo = await this.fetchIPFromEndpoint(endpoint);
        if (ipInfo) {
          this.cacheIP(ipInfo);
          Logger.log('🌐 Fetched IP address:', ipInfo.ip);
          return { ...ipInfo, source: 'api' };
        }
      } catch (error) {
        Logger.log(`⚠️ Failed to fetch IP from ${endpoint}:`, error);
        continue;
      }
    }

    // Fallback: use cached even if expired, or return unknown
    if (cached) {
      Logger.log('⚠️ Using expired cached IP as fallback:', cached.ip);
      return { ...cached, source: 'cached' };
    }

    // Last resort: return unknown IP
    const unknownIP: IpInfo = {
      ip: 'unknown',
      timestamp: Date.now(),
      source: 'api'
    };
    Logger.log('❌ Could not determine IP address, using unknown');
    return unknownIP;
  }

  private static async fetchIPFromEndpoint(endpoint: string): Promise<IpInfo | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(endpoint, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Handle different API response formats
      let ip: string;
      if (data.ip) {
        ip = data.ip;
      } else if (data.query) {
        ip = data.query;
      } else if (typeof data === 'string') {
        ip = data;
      } else {
        throw new Error('Invalid response format');
      }

      // Basic IP validation
      if (!this.isValidIP(ip)) {
        throw new Error(`Invalid IP format: ${ip}`);
      }

      return {
        ip,
        timestamp: Date.now(),
        source: 'api'
      };
    } catch (error) {
      throw error;
    }
  }

  private static getCachedIP(): IpInfo | null {
    try {
      const cached = localStorage.getItem(this.IP_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  private static cacheIP(ipInfo: IpInfo): void {
    try {
      localStorage.setItem(this.IP_CACHE_KEY, JSON.stringify(ipInfo));
    } catch (error) {
      Logger.log('⚠️ Failed to cache IP address:', error);
    }
  }

  private static isCacheValid(ipInfo: IpInfo): boolean {
    return (Date.now() - ipInfo.timestamp) < this.CACHE_DURATION;
  }

  private static isValidIP(ip: string): boolean {
    // Basic IP validation for both IPv4 and IPv6
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  }

  static clearIPCache(): void {
    try {
      localStorage.removeItem(this.IP_CACHE_KEY);
      Logger.log('🧹 IP cache cleared');
    } catch (error) {
      Logger.log('⚠️ Failed to clear IP cache:', error);
    }
  }
}